import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("caring_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // --- 1) Snapshot the ORIGINAL detail before we normalize it into a string.
    //     Downstream code (e.g. ZohoSettings) needs to inspect structured
    //     error bodies (like { code: "BOOKINGS_SCOPE_MISSING", message: ... }).
    let originalDetail = err?.response?.data?.detail;
    let structuredCode = null;
    if (originalDetail && typeof originalDetail === "object" && !Array.isArray(originalDetail) && originalDetail.code) {
      structuredCode = originalDetail.code;
    } else if (err?.response?.data && typeof err.response.data === "object" && err.response.data.code) {
      structuredCode = err.response.data.code;
    }
    if (err?.response?.data && typeof err.response.data === "object") {
      // Preserve raw structured payload so pages can still read `code`, etc.
      err.response.data.detail_raw = originalDetail;
    }

    // --- 2) Normalize FastAPI Pydantic validation errors (detail = list of
    //     objects) into a readable string so downstream `toast.error(...)` /
    //     render calls never receive raw objects/arrays (which would crash
    //     React). This only mutates `detail`, not `detail_raw`.
    try {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail !== "string") {
        let msg;
        if (Array.isArray(detail)) {
          msg = detail
            .map(d => {
              if (typeof d === "string") return d;
              const loc = Array.isArray(d?.loc) ? d.loc.filter(x => x !== "body").join(".") : "";
              const m = d?.msg || d?.message || JSON.stringify(d);
              return loc ? `${loc}: ${m}` : m;
            })
            .join(" · ");
        } else if (typeof detail === "object") {
          msg = detail.msg || detail.message || JSON.stringify(detail);
        } else {
          msg = String(detail);
        }
        err.response.data.detail = msg;
      }
    } catch (_) { /* ignore */ }

    // --- 3) Force-logout on 401, EXCEPT for expected structured errors coming
    //     from the Zoho Bookings scope negotiation. The Zoho Settings page
    //     catches these and shows a friendly "re-authorize" banner — logging
    //     the user out would hide that UX and break the recovery flow.
    if (err.response?.status === 401 && !window.location.pathname.startsWith("/estimate") && !window.location.pathname.startsWith("/proposal")) {
      const reqUrl = err.config?.url || "";
      const isBookingsScopeIssue =
        structuredCode === "BOOKINGS_SCOPE_MISSING" || reqUrl.includes("/zoho/bookings/");
      if (!isBookingsScopeIssue) {
        localStorage.removeItem("caring_token");
        localStorage.removeItem("caring_user");
        if (window.location.pathname !== "/") window.location.href = "/";
      }
    }
    return Promise.reject(err);
  }
);

export default api;

/**
 * Format a FastAPI/axios error into a human-readable string.
 * Handles: plain string detail, Pydantic validation array [{loc, msg, type, ...}],
 * and generic error objects. Always returns a string safe for React children.
 */
export const formatApiError = (err, fallback = "Ocorreu um erro") => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(d => {
        if (typeof d === "string") return d;
        const loc = Array.isArray(d?.loc) ? d.loc.filter(x => x !== "body").join(".") : "";
        const msg = d?.msg || d?.message || JSON.stringify(d);
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(" · ");
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
};

export const getUser = () => {
  const raw = localStorage.getItem("caring_user");
  return raw ? JSON.parse(raw) : null;
};

export const setSession = (token, user) => {
  localStorage.setItem("caring_token", token);
  localStorage.setItem("caring_user", JSON.stringify(user));
};

/**
 * Limpa toda a cache do browser (Cache API, Service Workers, IndexedDB,
 * sessionStorage e localStorage volátil) na abertura de uma nova sessão.
 * Preserva apenas o par (caring_token, caring_user) fresco que
 * ``setSession`` acabou de guardar.
 *
 * Chamado imediatamente após ``setSession`` no fluxo de login (ver
 * ``pages/Login.jsx``). Um user pode ver dados desactualizados quando
 * fica muito tempo sem fazer refresh — este mecanismo garante que
 * cada sessão começa com estado limpo do lado do cliente.
 *
 * Não afecta a cache HTTP do browser (não é acessível por JS); as
 * requests do axios já enviam ``Authorization`` e as respostas passam
 * pelo Kubernetes ingress + Cloudflare que não fazem cache de
 * ``/api/*``.
 */
export const clearAppCache = async () => {
  // Also preserve the user's chosen theme, remembered daily-popup snoozes,
  // and any other UX preferences whose loss would be jarring right after
  // login. Only volatile/derived data (cached fetches, workbox, indexedDB
  // snapshots) gets wiped.
  const keepKeys = new Set([
    "caring_token",
    "caring_user",
    "caring:theme",
    "caring:followupPopupSnoozedUntil",
    "caring:followupPopupLastSeenIds",
    "caring_last_cache_clear",
  ]);

  // 1. sessionStorage — apagar tudo
  try { window.sessionStorage.clear(); } catch (_) { /* ignore */ }

  // 2. localStorage — apagar tudo excepto token/user acabado de guardar
  try {
    for (const key of Object.keys(window.localStorage)) {
      if (!keepKeys.has(key)) window.localStorage.removeItem(key);
    }
  } catch (_) { /* ignore */ }

  // 3. Cache API (Service Workers + Workbox) — apagar todas as caches
  try {
    if (typeof caches !== "undefined" && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (_) { /* ignore */ }

  // 4. IndexedDB — apagar todas as bases de dados (Chrome/Edge/Firefox
  // modernos suportam ``indexedDB.databases()``)
  try {
    if (window.indexedDB && typeof indexedDB.databases === "function") {
      const dbs = await indexedDB.databases();
      await Promise.all((dbs || []).map((d) => new Promise((resolve) => {
        if (!d?.name) return resolve();
        const req = indexedDB.deleteDatabase(d.name);
        req.onblocked = req.onerror = req.onsuccess = () => resolve();
      })));
    }
  } catch (_) { /* ignore */ }

  // 5. Service Workers — desregistar todos para não intercepterem
  // requests com respostas antigas
  try {
    if (navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (_) { /* ignore */ }

  // Marca no localStorage a hora da última limpeza para telemetria/debug
  try {
    window.localStorage.setItem("caring_last_cache_clear", String(Date.now()));
  } catch (_) { /* ignore */ }
};

export const clearSession = () => {
  localStorage.removeItem("caring_token");
  localStorage.removeItem("caring_user");
};

// 25/02/2026 — Funil expandido para 16 statuses (pipeline comercial completo)
// + dimensão secundária ``onboarding_stage`` para o pós-adjudicação. Os
// códigos legados (``qualificacao_inicial``, ``rt_cancelada``, ``perdida``)
// mantêm-se aqui para continuar a renderizar leads históricos, mas os
// novos leads usam os códigos actualizados.
export const STATUS_LABELS = {
  // Pré-qualificação
  tentativa_contacto: "Tentativa de Contacto",
  contactado: "Contactado",
  qualificacao_inicial: "Tentativa de Contacto",   // legacy alias
  qualificado: "Qualificado",
  nao_qualificado: "Não Qualificado",
  // Estimativa
  estimativa_pronta: "Estimativa Pronta",
  estimativa_enviada: "Estimativa Enviada",
  perdido_estimativa: "Perdido na Estimativa",
  // Reunião Técnica
  qt_agendada: "RT Agendada",
  qt_realizada: "RT Realizada",
  qt_nao_realizada: "RT Não Realizada",
  rt_cancelada: "RT Não Realizada",                // legacy alias
  // Proposta
  proposta_pronta: "Proposta Pronta",
  proposta_enviada: "Proposta Enviada",
  aceite: "Proposta Aceite",                       // renamed label (was "Adjudicado")
  recusada: "Proposta Recusada",
  proposta_perdida: "Proposta Perdida",
  // Fecho
  negocio_ganho: "Negócio Ganho",
  negocio_perdido: "Negócio Perdido",
  perdida: "Negócio Perdido",                       // legacy alias
  sem_resposta: "Sem Resposta",
};

// 26/02/2026 — Helper que devolve o label do estado do lead com uma
// nuance semântica pedida pelo utilizador: enquanto ninguém tratou o
// lead (``first_contact_at`` vazio), o pill mostra "Por contactar" em
// vez de "Tentativa de Contacto". Assim que uma primeira tentativa
// (mesmo com insucesso) fica registada, o pill passa a "Tentativa de
// Contacto" — sinaliza que a equipa já actuou.
//
// Retornar sempre uma string não-vazia para não partir o UI:
// desconhecido → fallback para o próprio código do estado.
export function statusLabelForLead(lead) {
  if (!lead) return "";
  const status = lead.status;
  if ((status === "tentativa_contacto" || status === "qualificacao_inicial")
      && !lead.first_contact_at) {
    return "Por Contactar";
  }
  return STATUS_LABELS[status] || status || "";
}

export const STATUS_COLORS = {
  tentativa_contacto: "shift-chip",
  contactado: "shift-chip shift-chip-info",
  qualificacao_inicial: "shift-chip",
  qualificado: "shift-chip shift-chip-sage",
  nao_qualificado: "shift-chip shift-chip-danger",
  estimativa_pronta: "shift-chip shift-chip-warn",
  estimativa_enviada: "shift-chip shift-chip-info",
  perdido_estimativa: "shift-chip shift-chip-danger",
  qt_agendada: "shift-chip shift-chip-info",
  qt_realizada: "shift-chip shift-chip-info",
  qt_nao_realizada: "shift-chip shift-chip-danger",
  rt_cancelada: "shift-chip shift-chip-danger",
  proposta_pronta: "shift-chip shift-chip-warn",
  proposta_enviada: "shift-chip shift-chip-info",
  aceite: "shift-chip shift-chip-sage",
  recusada: "shift-chip shift-chip-danger",
  proposta_perdida: "shift-chip shift-chip-danger",
  negocio_ganho: "shift-chip shift-chip-sage",
  negocio_perdido: "shift-chip shift-chip-danger",
  perdida: "shift-chip shift-chip-danger",
  sem_resposta: "shift-chip shift-chip-warn",
};

// Dimensão pós-adjudicação (b3 do redesign do funil). Só é preenchida
// quando o negócio já foi ganho (``status ∈ {aceite, negocio_ganho}``).
// Renderizada como badge secundário ao lado do status comercial.
export const ONBOARDING_STAGE_LABELS = {
  pendente_caucao: "Pendente Caução",
  caucao_paga_recrutamento: "Caução Paga · Recrutamento",
  caucao_paga_total: "Caução Paga Total",
  pi_validado: "PI Validado por DT",
  servico_iniciado: "Serviço Iniciado",
};

export const ONBOARDING_STAGE_COLORS = {
  pendente_caucao: "shift-chip shift-chip-warn",
  caucao_paga_recrutamento: "shift-chip shift-chip-info",
  caucao_paga_total: "shift-chip shift-chip-info",
  pi_validado: "shift-chip shift-chip-sage",
  servico_iniciado: "shift-chip shift-chip-sage",
};

export const SERVICE_TYPES = [
  "Serviço Pontual", "Serviço Continuado", "Interna", "Externa", "Noites",
  "Fins de semana", "Hospital", "Pós-operatório", "Alzheimer", "Demência",
  "Cuidados paliativos", "Higiene", "Companhia", "Apoio doméstico", "Outro",
];

export const NEEDS = [
  "Higiene", "Alimentação", "Mobilizações", "Transferências",
  "Administração de medicação", "Companhia", "Passeios",
  "Apoio doméstico", "Estimulação Cognitiva", "Outro",
];

export const URGENCIES = [
  { value: "hoje", label: "Hoje" },
  { value: "24h", label: "24 horas" },
  { value: "48h", label: "48 horas" },
  { value: "semana", label: "Esta semana" },
  { value: "sem_urgencia", label: "Sem urgência" },
];

export const OBJECTIONS = ["Preço", "Família", "Tempo", "Outros concorrentes", "Sem decisão", "Outro"];

export const NEXT_ACTIONS = [
  { value: "telefonema", label: "Telefonema" },
  { value: "reuniao", label: "Nova reunião" },
  { value: "enviar_proposta", label: "Enviar proposta" },
  { value: "aguardar", label: "Aguardar decisão" },
  { value: "visita", label: "Visita presencial" },
];


/**
 * Download a Bearer-protected file via axios and trigger a browser download.
 * Works around the fact that plain <a href target="_blank"> cannot attach the
 * Authorization header and therefore hits 401 on any authenticated endpoint.
 *
 * @param {string} path   API path (e.g. "/leads/abc/recruitment-requisition/pdf")
 * @param {string} filename  Suggested filename shown to the user
 * @param {"inline"|"attachment"} [disposition]  Whether to open in a new tab or force download
 */
export async function downloadAuthedFile(path, filename, disposition = "inline") {
  const res = await api.get(path, { responseType: "blob" });
  const contentType = res.headers?.["content-type"] || "application/octet-stream";
  const blob = new Blob([res.data], { type: contentType });
  const url = URL.createObjectURL(blob);
  if (disposition === "attachment") {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    // Open in a new tab so the built-in PDF viewer picks it up (works around
    // Bearer auth by letting the browser open the object URL from memory).
    const win = window.open(url, "_blank", "noopener");
    if (!win) {
      // Popup blocker fallback — force a download anchor so the file still
      // reaches the user even when opening a new tab is denied.
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }
  // Give the browser a moment to grab the blob before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
